const REQUIRED_MESSAGE_FALLBACK = 'This is a required field.';
const EMAIL_MESSAGE_FALLBACK = 'Please enter a valid email address.';
/** Simple something@something.tld check (stricter than the browser's type=email). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class ContactUsForm extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string} */
  #message = REQUIRED_MESSAGE_FALLBACK;

  /** @type {string} */
  #emailMessage = EMAIL_MESSAGE_FALLBACK;

  /** @type {HTMLInputElement | null} */
  #email = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    this.#form = form;
    this.#message = this.dataset.requiredMessage || REQUIRED_MESSAGE_FALLBACK;
    this.#emailMessage = this.dataset.emailMessage || EMAIL_MESSAGE_FALLBACK;

    form.addEventListener('invalid', (event) => this.#onInvalid(event), { capture: true, signal });
    form.addEventListener('input', (event) => this.#onInput(event), { signal });
    form.addEventListener('change', (event) => this.#onInput(event), { signal });

    const email = form.querySelector('input[type="email"]');
    if (email instanceof HTMLInputElement) {
      this.#email = email;
      email.addEventListener('blur', () => this.#onEmailBlur(), { signal });
      this.#syncEmailValidity();
    }

    this.#focusNotice();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #focusNotice() {
    const notice = this.querySelector('.contact-us__success, .contact-us__error');
    if (!(notice instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      notice.scrollIntoView({ block: 'start' });
      notice.focus({ preventScroll: true });
    });
  }

  #syncEmailValidity() {
    if (!this.#email) return;
    const value = this.#email.value.trim();
    this.#email.setCustomValidity(value !== '' && !EMAIL_RE.test(value) ? 'invalid-email' : '');
  }

  /** @param {Event} event */
  #onInvalid(event) {
    const control = event.target;
    if (
      !(
        control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement ||
        control instanceof HTMLTextAreaElement
      )
    ) {
      return;
    }

    // Suppress the browser's default validation bubble; we render our own message.
    event.preventDefault();

    let message = this.#message;
    if (!control.validity.valueMissing) {
      if (control === this.#email) {
        message = this.#emailMessage;
      } else {
        // handled by contact-phone-validation.js
        return;
      }
    }

    this.#showError(control, message);
  }

  /** @param {Event} event */
  #onInput(event) {
    this.#clearField(event.target);
    if (event.target === this.#email) this.#syncEmailValidity();
  }

  #onEmailBlur() {
    if (!this.#email) return;
    this.#syncEmailValidity();
    const value = this.#email.value.trim();
    if (value !== '' && !EMAIL_RE.test(value)) {
      this.#showError(this.#email, this.#emailMessage);
    } else {
      this.#clearField(this.#email);
    }
  }

  /** @param {Element} control */
  #fieldOf(control) {
    return control.closest('.contact-us__field, .contact-us__consent');
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} control
   * @param {string} [message]
   */
  #showError(control, message = this.#message) {
    const field = this.#fieldOf(control);
    if (!field) return;

    control.setAttribute('aria-invalid', 'true');
    control.classList.add('contact-us__input--invalid');

    let error = field.querySelector(':scope > .contact-us__field-error');
    if (!(error instanceof HTMLElement)) {
      error = document.createElement('p');
      error.className = 'contact-us__field-error';
      error.setAttribute('role', 'alert');
      field.appendChild(error);
    }
    error.textContent = message;
    error.hidden = false;
  }

  /** @param {EventTarget | null} target */
  #clearField(target) {
    if (!(target instanceof HTMLElement)) return;
    const field = this.#fieldOf(target);
    if (!field) return;

    target.removeAttribute('aria-invalid');
    target.classList.remove('contact-us__input--invalid');

    const error = field.querySelector(':scope > .contact-us__field-error');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!customElements.get('contact-us-form')) {
  customElements.define('contact-us-form', ContactUsForm);
}
